import { formatDateDE } from "@/lib/playerConstants";

export const NOTE_VALUES = [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0];

export const SPIELTYPEN = [
  { key: "pflichtspiel", label: "Pflichtspiel", short: "Pflicht" },
  { key: "testspiel", label: "Testspiel", short: "Test" },
];

export const RATING_STATUS = [
  { key: "starter", label: "Starter" },
  { key: "ersatz", label: "Ersatz" },
];

export function spieltypLabel(key) {
  return SPIELTYPEN.find((s) => s.key === key)?.label || key;
}

export function noteColor(note) {
  if (note == null) return "text-zinc-500";
  if (note <= 2.0) return "text-emerald-400";
  if (note <= 3.0) return "text-lime-400";
  if (note <= 4.0) return "text-amber-400";
  if (note <= 5.0) return "text-orange-400";
  return "text-rose-400";
}

export function noteBg(note) {
  if (note == null) return "bg-zinc-800";
  if (note <= 2.0) return "bg-emerald-500";
  if (note <= 3.0) return "bg-lime-500";
  if (note <= 4.0) return "bg-amber-500";
  if (note <= 5.0) return "bg-orange-500";
  return "bg-rose-500";
}

export { formatDateDE };

// Wäsche helpers
export function washStats(playerId, matches) {
  let count = 0;
  let lastDate = null;
  for (const m of matches) {
    if (m.waesche_player_id === playerId) {
      count++;
      if (!lastDate || m.datum > lastDate) lastDate = m.datum;
    }
  }
  return { count, lastDate };
}

export function teamWashStats(players, matches) {
  const ranking = players.map((p) => ({ player: p, ...washStats(p.id, matches) }))
    .sort((a, b) => {
      if (a.count !== b.count) return a.count - b.count;
      // tie: oldest lastDate first (null = never → highest priority)
      if (a.lastDate == null && b.lastDate == null) return 0;
      if (a.lastDate == null) return -1;
      if (b.lastDate == null) return 1;
      return a.lastDate.localeCompare(b.lastDate);
    });
  const counts = ranking.map((r) => r.count);
  const min = counts.length ? Math.min(...counts) : 0;
  const max = counts.length ? Math.max(...counts) : 0;
  return { ranking, min, max, diff: max - min };
}

export function suggestNextWashPlayer(players, matches) {
  if (players.length === 0) return null;
  const stats = teamWashStats(players, matches);
  const minCount = stats.min;
  const candidates = stats.ranking.filter((r) => r.count === minCount);
  if (candidates.length === 0) return null;
  // Tiebreak: oldest lastDate first (already sorted that way). If multiple with same date or none, pick random.
  const firstDate = candidates[0].lastDate;
  const sameDate = candidates.filter((r) => r.lastDate === firstDate);
  return sameDate[Math.floor(Math.random() * sameDate.length)].player;
}

// Average of an array of notes (numbers)
function avg(values) {
  if (values.length === 0) return null;
  return Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 10) / 10;
}

export function matchTeamAvg(match) {
  const notes = Object.values(match.ratings || {}).map((r) => Number(r.note)).filter((n) => !Number.isNaN(n));
  return avg(notes);
}

// All stats for a single player based on all matches
export function playerMatchStats(playerId, matches) {
  // Only matches where player was rated
  const rated = [];
  for (const m of matches) {
    const r = (m.ratings || {})[playerId];
    if (r && typeof r.note === "number") {
      rated.push({ match: m, rating: r });
    }
  }
  rated.sort((a, b) => a.match.datum.localeCompare(b.match.datum));

  const allNotes = rated.map((r) => r.rating.note);
  const seasonAvg = avg(allNotes);

  // Current month
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const monthNotes = rated.filter((r) => r.match.datum.startsWith(ym)).map((r) => r.rating.note);
  const monthAvg = avg(monthNotes);

  // By spieltyp
  const pflichtNotes = rated.filter((r) => r.match.spieltyp === "pflichtspiel").map((r) => r.rating.note);
  const testNotes = rated.filter((r) => r.match.spieltyp === "testspiel").map((r) => r.rating.note);

  // Best / worst
  let best = null, worst = null;
  for (const item of rated) {
    if (!best || item.rating.note < best.rating.note) best = item;
    if (!worst || item.rating.note > worst.rating.note) worst = item;
  }

  // Trend: compare avg of last 3 vs prior 3
  const last3 = rated.slice(-3).map((r) => r.rating.note);
  const prev3 = rated.slice(-6, -3).map((r) => r.rating.note);
  const last3Avg = avg(last3);
  const prev3Avg = avg(prev3);
  let trend = "stabil";
  if (last3Avg != null && prev3Avg != null) {
    const diff = last3Avg - prev3Avg;
    if (diff < -0.2) trend = "steigend"; // lower note = better
    else if (diff > 0.2) trend = "fallend";
  }

  // Form curve: last 10 matches
  const formCurve = rated.slice(-10).map((r) => ({
    datum: r.match.datum,
    note: r.rating.note,
    spieltyp: r.match.spieltyp,
  }));

  // Starter vs Ersatz counts
  const starterCount = rated.filter((r) => r.rating.status === "starter").length;
  const ersatzCount = rated.filter((r) => r.rating.status === "ersatz").length;

  return {
    total: rated.length,
    seasonAvg,
    monthAvg,
    pflichtAvg: avg(pflichtNotes),
    pflichtCount: pflichtNotes.length,
    testAvg: avg(testNotes),
    testCount: testNotes.length,
    best, worst,
    trend,
    last3Avg, prev3Avg,
    formCurve,
    starterCount,
    ersatzCount,
  };
}

// Team-level aggregation across matches
export function teamMatchStats(players, matches) {
  const sortedMatches = [...matches].sort((a, b) => a.datum.localeCompare(b.datum));
  const perMatchTeamAvg = sortedMatches.map((m) => ({
    id: m.id,
    datum: m.datum,
    gegner: m.gegner,
    spieltyp: m.spieltyp,
    teamAvg: matchTeamAvg(m),
    ratedCount: Object.keys(m.ratings || {}).length,
  }));

  // Per-player season avg → ranking
  const perPlayer = players
    .map((p) => ({ player: p, stats: playerMatchStats(p.id, matches) }))
    .filter((p) => p.stats.total > 0);

  const ranking = [...perPlayer].sort((a, b) => {
    if (a.stats.seasonAvg == null) return 1;
    if (b.stats.seasonAvg == null) return -1;
    return a.stats.seasonAvg - b.stats.seasonAvg; // lower is better
  });

  // Starter vs Ersatz comparison
  let starterSum = 0, starterN = 0, ersatzSum = 0, ersatzN = 0;
  for (const m of matches) {
    for (const r of Object.values(m.ratings || {})) {
      if (typeof r.note !== "number") continue;
      if (r.status === "starter") { starterSum += r.note; starterN++; }
      else if (r.status === "ersatz") { ersatzSum += r.note; ersatzN++; }
    }
  }
  const starterAvg = starterN > 0 ? Math.round((starterSum / starterN) * 10) / 10 : null;
  const ersatzAvg = ersatzN > 0 ? Math.round((ersatzSum / ersatzN) * 10) / 10 : null;

  // Overall team avg
  const teamAvgValues = perMatchTeamAvg.map((m) => m.teamAvg).filter((v) => v != null);
  const teamOverallAvg = avg(teamAvgValues);

  // Trend: last 3 matches vs prior 3
  const last3Team = perMatchTeamAvg.slice(-3).map((m) => m.teamAvg).filter((v) => v != null);
  const prev3Team = perMatchTeamAvg.slice(-6, -3).map((m) => m.teamAvg).filter((v) => v != null);

  return {
    totalMatches: matches.length,
    teamOverallAvg,
    perMatchTeamAvg,
    ranking,
    starterAvg,
    ersatzAvg,
    starterCount: starterN,
    ersatzCount: ersatzN,
    last3TeamAvg: avg(last3Team),
    prev3TeamAvg: avg(prev3Team),
  };
}
